import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Shield, CheckCircle, Clock, Share2, ArrowLeft, Copy, Building2, FileText, User, Mail, MapPin } from 'lucide-react';
import { api } from '../App';
import { toast } from 'sonner';
import StripeCheckout from '../components/StripeCheckout';
import Footer from '../components/Footer';

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 
  'Wisconsin', 'Wyoming'
];

const packages = [
  {
    id: 'standard',
    name: 'Standard',
    price: 49,
    description: 'Basic background verification',
    features: ['Identity Verification', 'Criminal Record Check', 'Sex Offender Registry']
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 79,
    description: 'Comprehensive professional screening',
    features: ['All Standard features', 'Employment Verification', 'Education Verification', 'Reference Check']
  },
  {
    id: 'comprehensive',
    name: 'Comprehensive',
    price: 129,
    description: 'Complete background investigation',
    features: ['All Professional features', 'Credit History', 'Driving Record', 'Social Media Review', 'Drug Test Coordination']
  }
];

export default function BackgroundCheck({ user }) {
  const [myChecks, setMyChecks] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState('standard');
  const [showPayment, setShowPayment] = useState(false);
  const [checkData, setCheckData] = useState(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [showInfoForm, setShowInfoForm] = useState(false);
  const [submittingInfo, setSubmittingInfo] = useState(false);
  const [infoFormData, setInfoFormData] = useState({
    email: user?.email || '',
    first_name: '',
    last_name: '',
    state: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMyChecks = async () => {
      try {
        const res = await api.get('/background-checks/my-checks');
        setMyChecks(res.data);
      } catch (error) {
        console.error('Failed to load checks');
      }
    };
    
    if (user) {
      fetchMyChecks();
    }
  }, [user]);

  const loadMyChecks = async () => {
    try {
      const res = await api.get('/background-checks/my-checks');
      setMyChecks(res.data);
    } catch (error) {
      console.error('Failed to load checks');
    }
  };

  const handleInitiateCheck = async () => {
    try {
      const res = await api.post('/background-checks/initiate', {
        package: selectedPackage
      });
      setCheckData(res.data);
      setShowPayment(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate background check');
    }
  };

  const handlePaymentSuccess = async () => {
    try {
      // After payment, show the info form instead of immediately confirming
      setShowPayment(false);
      setShowInfoForm(true);
      toast.success('Payment successful! Please complete your information.');
    } catch (error) {
      toast.error('Failed to process payment');
    }
  };

  const handleInfoFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!infoFormData.email || !infoFormData.first_name || !infoFormData.last_name || !infoFormData.state) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmittingInfo(true);
    try {
      // Submit the info along with the check confirmation
      await api.post(`/background-checks/${checkData.check.id}/confirm`, {
        email: infoFormData.email,
        first_name: infoFormData.first_name,
        last_name: infoFormData.last_name,
        state: infoFormData.state
      });
      
      toast.success('Background check submitted! Our team will process your request within 2-3 business days.');
      setShowInfoForm(false);
      setCheckData(null);
      setInfoFormData({
        email: user?.email || '',
        first_name: '',
        last_name: '',
        state: ''
      });
      loadMyChecks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit information');
    } finally {
      setSubmittingInfo(false);
    }
  };

  const copyShareableLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success('Shareable link copied to clipboard!');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending_payment': { variant: 'outline', label: 'Pending Payment' },
      'pending_review': { variant: 'secondary', label: 'Pending Review' },
      'initiated': { variant: 'secondary', label: 'Initiated' },
      'processing': { variant: 'default', label: 'Processing' },
      'completed': { variant: 'success', label: 'Completed' },
      'failed': { variant: 'destructive', label: 'Failed' }
    };
    const config = statusConfig[status] || { variant: 'outline', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const hasActiveCheck = myChecks.some(c => 
    ['pending_payment', 'pending_review', 'initiated', 'processing', 'completed'].includes(c.status)
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft size={20} />
            </Button>
            <div className="flex items-center gap-3">
              <img src="/logo.svg" alt="The Interview Logo" className="w-10 h-10" />
              <h1 className="text-2xl font-heading font-bold">Background Check</h1>
            </div>
          </div>
          <div className="text-sm text-muted-foreground">{user?.full_name}</div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Hero */}
        <div className="mb-8 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield size={32} className="text-blue-600" />
          </div>
          <h2 className="text-3xl font-heading font-bold mb-2">Verified Background Checks</h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Get a verified background check powered by Checkr. Share your results with potential employers to stand out.
          </p>
        </div>

        {/* My Checks */}
        {myChecks.length > 0 && (
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4">Your Background Checks</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {myChecks.map((check) => (
                <Card key={check.id} data-testid={`check-card-${check.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg capitalize">{check.package} Package</CardTitle>
                        <CardDescription>
                          Initiated: {new Date(check.created_at).toLocaleDateString()}
                        </CardDescription>
                      </div>
                      {getStatusBadge(check.status)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {check.status === 'pending_review' && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                        <Clock size={14} />
                        <span>Your information is being reviewed. Our team will process your request within 1-2 business days.</span>
                      </div>
                    )}
                    
                    {check.status === 'processing' && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                        <Clock size={14} className="animate-pulse" />
                        <span>Processing... Results typically ready in 3-5 business days</span>
                      </div>
                    )}
                    
                    {check.status === 'completed' && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-green-600">
                          <CheckCircle size={16} />
                          <span className="text-sm font-medium">Check completed successfully</span>
                        </div>
                        
                        {check.shareable_link && (
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-xs text-muted-foreground mb-2">Shareable Link:</p>
                            <div className="flex items-center gap-2">
                              <code className="text-xs bg-white px-2 py-1 rounded border flex-1 overflow-hidden">
                                {check.shareable_link}
                              </code>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => copyShareableLink(check.shareable_link)}
                                data-testid={`copy-link-${check.id}`}
                              >
                                <Copy size={14} />
                              </Button>
                            </div>
                          </div>
                        )}
                        
                        {check.shared_with_companies?.length > 0 && (
                          <div className="text-xs text-muted-foreground">
                            <Building2 size={12} className="inline mr-1" />
                            Shared with {check.shared_with_companies.length} companies
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Package Selection */}
        {!hasActiveCheck && (
          <>
            <h3 className="text-xl font-semibold mb-4">Choose Your Package</h3>
            <RadioGroup value={selectedPackage} onValueChange={setSelectedPackage} className="grid md:grid-cols-3 gap-4">
              {packages.map((pkg) => (
                <div key={pkg.id}>
                  <RadioGroupItem value={pkg.id} id={pkg.id} className="peer sr-only" />
                  <Label
                    htmlFor={pkg.id}
                    className="flex flex-col h-full cursor-pointer rounded-lg border-2 border-muted bg-white p-6 hover:border-primary peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5"
                    data-testid={`package-${pkg.id}`}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h4 className="font-semibold text-lg">{pkg.name}</h4>
                        <p className="text-sm text-muted-foreground">{pkg.description}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold">${pkg.price}</div>
                        <div className="text-xs text-muted-foreground">one-time</div>
                      </div>
                    </div>
                    <ul className="space-y-2 text-sm flex-1">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <CheckCircle size={14} className="text-green-500 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </Label>
                </div>
              ))}
            </RadioGroup>

            <div className="mt-8 text-center">
              <Button 
                size="lg" 
                onClick={handleInitiateCheck}
                className="px-8"
                data-testid="initiate-check-btn"
              >
                <Shield size={18} className="mr-2" />
                Start Background Check - ${packages.find(p => p.id === selectedPackage)?.price}
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                Powered by Checkr • Results in 3-5 business days
              </p>
            </div>
          </>
        )}

        {hasActiveCheck && !myChecks.some(c => c.status === 'completed') && (
          <div className="text-center py-8">
            <p className="text-muted-foreground">You already have an active background check in progress.</p>
          </div>
        )}
      </div>

      {/* Payment Dialog */}
      <Dialog open={showPayment} onOpenChange={setShowPayment}>
        <DialogContent className="max-w-md" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle>Complete Payment</DialogTitle>
            <DialogDescription>
              {selectedPackage.charAt(0).toUpperCase() + selectedPackage.slice(1)} Package - ${checkData?.amount}
            </DialogDescription>
          </DialogHeader>
          
          {checkData && (
            <StripeCheckout 
              clientSecret={checkData.client_secret}
              onSuccess={handlePaymentSuccess}
              onCancel={() => setShowPayment(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Information Form Dialog - Shown after payment */}
      <Dialog open={showInfoForm} onOpenChange={(open) => !submittingInfo && setShowInfoForm(open)}>
        <DialogContent className="max-w-md" data-testid="info-form-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="text-blue-600" size={20} />
              Complete Your Information
            </DialogTitle>
            <DialogDescription>
              Please provide your details so our team can process your background check through Checkr.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleInfoFormSubmit} className="space-y-4">
            <div>
              <Label htmlFor="bc-email" className="flex items-center gap-2">
                <Mail size={14} /> Email Address *
              </Label>
              <Input
                id="bc-email"
                type="email"
                value={infoFormData.email}
                onChange={(e) => setInfoFormData({...infoFormData, email: e.target.value})}
                placeholder="your.email@example.com"
                required
                data-testid="bc-email-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                You&apos;ll receive consent forms and status updates at this email.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="bc-first-name" className="flex items-center gap-2">
                  <User size={14} /> First Name *
                </Label>
                <Input
                  id="bc-first-name"
                  type="text"
                  value={infoFormData.first_name}
                  onChange={(e) => setInfoFormData({...infoFormData, first_name: e.target.value})}
                  placeholder="John"
                  required
                  data-testid="bc-first-name-input"
                />
              </div>
              <div>
                <Label htmlFor="bc-last-name">Last Name *</Label>
                <Input
                  id="bc-last-name"
                  type="text"
                  value={infoFormData.last_name}
                  onChange={(e) => setInfoFormData({...infoFormData, last_name: e.target.value})}
                  placeholder="Doe"
                  required
                  data-testid="bc-last-name-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="bc-state" className="flex items-center gap-2">
                <MapPin size={14} /> State of Residence *
              </Label>
              <Select
                value={infoFormData.state}
                onValueChange={(value) => setInfoFormData({...infoFormData, state: value})}
              >
                <SelectTrigger data-testid="bc-state-select">
                  <SelectValue placeholder="Select your state" />
                </SelectTrigger>
                <SelectContent>
                  {US_STATES.map((state) => (
                    <SelectItem key={state} value={state}>{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Background check requirements vary by state.
              </p>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg text-sm">
              <h4 className="font-medium text-blue-900 mb-2">What happens next?</h4>
              <ol className="text-blue-800 space-y-1 list-decimal list-inside">
                <li>Our team will create your background check in Checkr</li>
                <li>You&apos;ll receive an email from Checkr to provide consent</li>
                <li>Complete any additional verification steps required</li>
                <li>Results typically ready within 2-5 business days</li>
              </ol>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setShowInfoForm(false)}
                disabled={submittingInfo}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={submittingInfo}
                data-testid="submit-info-btn"
              >
                {submittingInfo ? 'Submitting...' : 'Submit Information'}
              </Button>
            </div>

            <p className="text-xs text-center text-muted-foreground">
              By submitting, you agree to our{' '}
              <button 
                type="button"
                onClick={() => navigate('/background-check-policy')}
                className="text-blue-600 hover:underline"
              >
                Background Check Policy
              </button>
            </p>
          </form>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
